import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Pending request deduplication — cancel identical in-flight requests
const pendingRequests = new Map();

const getRequestKey = (config) => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params || {})}:${JSON.stringify(config.data || {})}`;
};

// Create axios instance
const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30s default timeout to prevent hanging requests
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request deduplication interceptor
api.interceptors.request.use(
  (config) => {
    const key = getRequestKey(config);
    if (pendingRequests.has(key)) {
      const controller = pendingRequests.get(key);
      controller.abort();
    }
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(key, controller);
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    const key = getRequestKey(response.config);
    pendingRequests.delete(key);
    return response;
  },
  (error) => {
    if (error.config) {
      const key = getRequestKey(error.config);
      pendingRequests.delete(key);
    }
    return Promise.reject(error);
  }
);

// Function to set auth token
let getAuthToken = null;

export const setAuthTokenGetter = (getter) => {
  getAuthToken = getter;
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  async (config) => {
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// User API
export const getCurrentUser = async () => {
  const response = await api.get('/users/me');
  return response.data;
};

export const updateUser = async (userData) => {
  const response = await api.put('/users/me', userData);
  return response.data;
};

// Task API
export const createTask = async (taskData) => {
  const response = await api.post('/tasks', taskData);
  return response.data;
};

export const getTasks = async (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  const response = await api.get(`/tasks${params ? '?' + params : ''}`);
  return response.data;
};

export const getTask = async (taskId) => {
  const response = await api.get(`/tasks/${taskId}`);
  return response.data;
};

export const updateTask = async (taskId, taskData) => {
  const response = await api.put(`/tasks/${taskId}`, taskData);
  return response.data;
};

export const deleteTask = async (taskId) => {
  const response = await api.delete(`/tasks/${taskId}`);
  return response.data;
};

// Chat API - AGENTIC CHATBOT
export const processMessage = async (message) => {
  const response = await api.post('/chat/process', { message });
  return response.data;
};

export const createTasksWithSchedule = async (query, answers = {}) => {
  const response = await api.post('/chat/create-tasks', { query, answers });
  return response.data;
};

export const confirmTasks = async (tasks) => {
  const response = await api.post('/chat/confirm-tasks', { tasks });
  return response.data;
};

export const editTaskViaChat = async (taskId, instruction) => {
  const response = await api.post('/chat/edit-task', { task_id: taskId, instruction });
  return response.data;
};

export const confirmTaskEdit = async (taskId, updates) => {
  const response = await api.post('/chat/confirm-edit', { task_id: taskId, updates });
  return response.data;
};

export const completeTaskViaChat = async (taskId) => {
  const response = await api.post('/chat/complete-task', { task_id: taskId });
  return response.data;
};

export const deleteTaskViaChat = async (taskId) => {
  const response = await api.post('/chat/delete-task', { task_id: taskId });
  return response.data;
};

export const viewTasksSummary = async (query) => {
  const response = await api.post('/chat/view-tasks', { query });
  return response.data;
};

// Legacy chat APIs (for backward compatibility)
export const analyzeQuery = async (message) => {
  const response = await api.post('/chat/analyze', { message });
  return response.data;
};

export const createActionablePreview = async (originalQuery, answers = {}) => {
  const response = await api.post('/chat/create-preview', {
    original_query: originalQuery,
    answers: answers
  });
  return response.data;
};

export const editActionableItem = async (item, editInstruction) => {
  const response = await api.post('/chat/edit-item', {
    item: item,
    edit_instruction: editInstruction
  });
  return response.data;
};

export const approveActionableItems = async (items) => {
  const response = await api.post('/chat/approve-items', { items });
  return response.data;
};

// Legacy chat (for general conversation)
export const sendChatMessage = async (message) => {
  const response = await api.post('/chat', { message });
  return response.data;
};

export const getChatHistory = async (limit = 50) => {
  const response = await api.get(`/chat/history?limit=${limit}`);
  return response.data;
};

// AI Assistant API
export const breakdownTask = async (taskId) => {
  const response = await api.post('/ai/breakdown-task', null, {
    params: { task_id: taskId }
  });
  return response.data;
};

export const suggestSchedule = async (taskId) => {
  const response = await api.post('/ai/suggest-schedule', null, {
    params: { task_id: taskId }
  });
  return response.data;
};

export const detectConflicts = async () => {
  const response = await api.get('/ai/detect-conflicts');
  return response.data;
};

// Calendar API
export const getWeekSchedule = async (startDate = null) => {
  const params = startDate ? `?start_date=${startDate}` : '';
  const response = await api.get(`/calendar/week${params}`);
  return response.data;
};

// Settings API
export const updateSettings = async (settings) => {
  const response = await api.put('/settings', settings);
  return response.data;
};

// Public Contact & Feedback API
export const submitContact = async (contactData) => {
  const response = await api.post('/contact', contactData);
  return response.data;
};

export const submitFeedback = async (feedbackData) => {
  const isFormData = feedbackData instanceof FormData;
  const response = await api.post('/feedback', feedbackData, {
    headers: {
      'Content-Type': isFormData ? 'multipart/form-data' : 'application/json'
    }
  });
  return response.data;
};

export default api;
